import React from "react";
import { trackingProviderCaptureClick } from "@origin-digital/reporting-client";
import {
  OriginThemeProvider,
  TrackingProvider,
} from "@origin-digital/ods-core";
import { LinkComponentProps } from "@origin-digital/ods-types";
import { parseMarkdownEvent } from "@origin-digital/event-dispatcher";
import { UserContextProvider } from "@origin-digital/user-context-provider";
import {
  FeatureFlagContextProvider,
  useFeatureFlag,
} from "@origin-digital/feature-flags";
import { EnvironmentNames } from "@origin-digital/platform-enums";
import { useTokenShield } from "@origin-digital/token-shield";
import {
  FontStyles,
  ResetStyles,
  IconFontStyles,
  BrazeStyles,
} from "../styles/global";
import { FEATURE_FLAGS, getDefaultFeatures } from "../client/features";

type Props = {
  setupFonts?: boolean;
  children: React.ReactNode;
};

const MeshLink = React.forwardRef<any, LinkComponentProps>((props, ref) => {
  const { href, onClick } = parseMarkdownEvent(props.href);
  const { children, ...rest } = props;
  return (
    <a
      ref={ref}
      {...rest}
      href={href}
      onClick={(e) => {
        if (onClick) {
          onClick(e);
        }
        return rest.onClick && rest.onClick(e);
      }}
    >
      {children}
    </a>
  );
});

/*
 * This messy pair of components is used to support feature toggling of TokenShield with environment config.
 * Because it's all implemented as hooks, conditionally toggling them isn't possible, so we wrap it in some components to make it easier
 * TODO: Delete this after tokenshield rollout is complete, and call hook in rootwrapper directly
 */
export const TokenShieldToggler: React.FC = () => {
  const enabled = useFeatureFlag(FEATURE_FLAGS.TOKEN_SHIELD);
  const TokenShield: React.FC = () => {
    useTokenShield();
    return <></>;
  };
  if (enabled) {
    return <TokenShield />;
  }
  return <></>;
};

export default ({ setupFonts = true, children }: Props) => {
  // Guard against SSR error in tal-core
  const env =
    typeof window === "undefined"
      ? undefined
      : (window?.oetal?.env?.name as EnvironmentNames);

  return (
    <>
      {setupFonts && (
        <>
          <FontStyles />
          <ResetStyles />
          <IconFontStyles />
          <BrazeStyles />
        </>
      )}
      <OriginThemeProvider
        //@ts-ignore fails when updating to ts 4
        linkComponent={MeshLink}
        generatorClassNameOptions={{ seed: "tal" }}
      >
        <FeatureFlagContextProvider featureFlags={getDefaultFeatures(env)}>
          <>
            <TokenShieldToggler />
            <UserContextProvider>
              <TrackingProvider
                onTrackingCapture={trackingProviderCaptureClick}
              >
                {children}
              </TrackingProvider>
            </UserContextProvider>
          </>
        </FeatureFlagContextProvider>
      </OriginThemeProvider>
    </>
  );
};
